const EARTH_RADIUS_METERS = 6_371_000

export const normalizeMapAddress = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+-\s+/g, ',')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

export const distanceBetweenCoordinates = (first, second) => {
  if (!first || !second) return Number.POSITIVE_INFINITY
  const toRadians = (degrees) => (degrees * Math.PI) / 180
  const latDelta = toRadians(second.lat - first.lat)
  const lngDelta = toRadians(second.lng - first.lng)
  const firstLat = toRadians(first.lat)
  const secondLat = toRadians(second.lat)
  const haversine =
    Math.sin(latDelta / 2) ** 2 + Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(lngDelta / 2) ** 2
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(haversine))
}

const getComparablePosition = (property) => {
  if (!Array.isArray(property?.coordinates) || property.coordinates.length !== 2) return null
  const lng = Number(property.coordinates[0])
  const lat = Number(property.coordinates[1])
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
}

export const groupComparableMarkers = (properties = [], subjectPosition, subjectAddress, thresholdMeters = 5) => {
  const normalizedSubjectAddress = normalizeMapAddress(subjectAddress)
  const groups = []

  properties.forEach((property) => {
    const position = getComparablePosition(property)
    if (!position) return
    const normalizedAddress = normalizeMapAddress(property.location)
    const subjectDistance = distanceBetweenCoordinates(position, subjectPosition)
    const sameSubjectAddress = normalizedAddress && normalizedSubjectAddress && normalizedAddress === normalizedSubjectAddress
    const matchesSubject =
      subjectDistance <= thresholdMeters || (sameSubjectAddress && subjectDistance <= 25)
    const group = groups.find(
      (candidate) => {
        const distance = distanceBetweenCoordinates(position, candidate.position)
        const sameAddress = normalizedAddress && candidate.normalizedAddress && normalizedAddress === candidate.normalizedAddress
        return distance <= thresholdMeters || (sameAddress && distance <= 25)
      }
    )

    if (group) {
      group.comparables.push(property)
      group.containsSubject = group.containsSubject || matchesSubject
      const count = group.comparables.length
      group.position = {
        lat: (group.position.lat * (count - 1) + position.lat) / count,
        lng: (group.position.lng * (count - 1) + position.lng) / count,
      }
      return
    }

    groups.push({
      position,
      normalizedAddress,
      containsSubject: matchesSubject,
      comparables: [property],
    })
  })

  return groups
}

export const getExpandedMarkerPositions = (origin, count) => {
  const radiusMeters = Math.min(22, 11 + Math.max(0, count - 2) * 1.7)
  const metersPerLatitudeDegree = 111_320
  const metersPerLongitudeDegree = metersPerLatitudeDegree * Math.max(0.2, Math.cos((origin.lat * Math.PI) / 180))

  return Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI * 2) / count
    return {
      lat: origin.lat + (Math.sin(angle) * radiusMeters) / metersPerLatitudeDegree,
      lng: origin.lng + (Math.cos(angle) * radiusMeters) / metersPerLongitudeDegree,
    }
  })
}
